import React, { useState, useEffect, useCallback } from "react";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import { toast } from "react-toastify";
import { eventService, dailyItemService } from "../services/authService";
import EventModal from "./EventModal";
import DeliveryEventModal from "./DeliveryEventModal";
import "react-big-calendar/lib/css/react-big-calendar.css";

const localizer = momentLocalizer(moment);

const CalendarView = ({ user }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedDeliveryEvent, setSelectedDeliveryEvent] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [calendarDate, setCalendarDate] = useState(new Date());

  const fetchEvents = useCallback(async () => {
    setLoading(true);

    // Get start and end of the month for better performance
    const startOfMonth = moment(calendarDate).startOf("month").toISOString();
    const endOfMonth = moment(calendarDate).endOf("month").toISOString();

    // Fetch regular events
    let eventsData = [];
    try {
      if (user.role === "admin") {
        eventsData = await eventService.getAllEventsAdmin(
          startOfMonth,
          endOfMonth
        );
      } else {
        eventsData = await eventService.getAllEvents(startOfMonth, endOfMonth);
      }
      console.log("Events fetched successfully:", eventsData.length);
    } catch (error) {
      console.error("Error fetching regular events:", error);
      console.error(
        "Regular events error details:",
        error.response?.data || error.message
      );
      eventsData = [];
    }

    // Fetch daily items/delivery events
    let dailyItemsData = [];
    try {
      if (user.role === "admin") {
        dailyItemsData = await dailyItemService.getAllDailyItemsAdmin(
          startOfMonth,
          endOfMonth
        );
      } else {
        dailyItemsData = await dailyItemService.getUserDailyItems(
          startOfMonth,
          endOfMonth
        );
      }
      console.log("Daily items fetched successfully:", dailyItemsData.length);
    } catch (error) {
      console.error("Error fetching daily items:", error);
      console.error(
        "Daily items error details:",
        error.response?.data || error.message
      );
      // Don't fail the whole component if daily items fail
      dailyItemsData = [];
    }

    // Transform regular events for react-big-calendar
    const transformedEvents = eventsData.map((event) => ({
      id: event._id,
      title: event.title || "Untitled Event",
      start: new Date(event.start),
      end: new Date(event.end),
      allDay: event.allDay,
      resource: {
        ...event,
        type: "event",
        color: event.color || "#3174ad",
        createdBy: event.createdBy,
        isOwner: event.createdBy?._id === user.id,
      },
    }));

    // Transform daily items into calendar events (skip for admin users)
    const deliveryEvents = [];

    // Only add delivery events for non-admin users
    if (user.role !== "admin") {
      dailyItemsData.forEach((dailyItem) => {
        if (!dailyItem || !dailyItem._id) {
          console.warn("Invalid daily item:", dailyItem);
          return;
        }

        const startDate = moment(dailyItem.startDate);
        const endDate = moment(dailyItem.endDate);

        // Generate events for each day in the date range
        let currentDate = startDate.clone();
        while (currentDate.isSameOrBefore(endDate, "day")) {
          // Parse delivery time from dailyItem (format: "HH:MM")
          const deliveryTime = dailyItem.deliveryTime || "09:00";
          const [hours, minutes] = deliveryTime.split(":").map(Number);

          deliveryEvents.push({
            id: `delivery_${dailyItem._id}_${currentDate.format("YYYY-MM-DD")}`,
            title: `📦 ${dailyItem.title || "Unknown Item"}`,
            start: currentDate.clone().hour(hours).minute(minutes).toDate(),
            end: currentDate
              .clone()
              .hour(hours)
              .minute(minutes + 60)
              .toDate(), // 1 hour duration
            allDay: false,
            resource: {
              type: "delivery",
              dailyItem: dailyItem,
              deliveryDate: currentDate.format("YYYY-MM-DD"),
              color: "#28a745", // Green for deliveries
              isOwner:
                user.role === "admin" || dailyItem.assignedTo?._id === user.id,
              user: dailyItem.assignedTo,
            },
          });
          currentDate.add(1, "day");
        }
      });
    }

    setEvents([...transformedEvents, ...deliveryEvents]);
    setLoading(false);
  }, [calendarDate, user.role, user.id]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handleSelectEvent = (event) => {
    if (event.resource.type === "delivery") {
      setSelectedDeliveryEvent(event.resource);
      setShowDeliveryModal(true);
    } else {
      setSelectedEvent(event.resource);
      setSelectedSlot(null);
      setShowModal(true);
    }
  };

  const handleSelectSlot = (slotInfo) => {
    setSelectedEvent(null);
    setSelectedSlot(slotInfo);
    setShowModal(true);
  };

  const handleEventSaved = () => {
    setShowModal(false);
    setShowDeliveryModal(false);
    setSelectedEvent(null);
    setSelectedDeliveryEvent(null);
    setSelectedSlot(null);
    fetchEvents();
  };

  const handleDeleteEvent = async (eventId) => {
    if (window.confirm("Are you sure you want to delete this event?")) {
      try {
        await eventService.deleteEvent(eventId);
        toast.success("Event deleted successfully");
        fetchEvents();
      } catch (error) {
        console.error("Error deleting event:", error);
        toast.error(error.response?.data?.message || "Failed to delete event");
      }
    }
  };

  const eventStyleGetter = (event, start, end, isSelected) => {
    const backgroundColor = event.resource?.color || "#3174ad";
    const isOwner = event.resource?.isOwner;
    const opacity = isOwner ? 1 : 0.7;
    const isDelivery = event.resource?.type === "delivery";

    return {
      style: {
        backgroundColor,
        borderRadius: "5px",
        opacity,
        color: "white",
        border: isDelivery ? "2px solid #20c997" : "0px",
        display: "block",
        fontSize: "12px",
        fontWeight: isOwner ? "bold" : "normal",
      },
    };
  };

  const CustomEvent = ({ event }) => {
    const isOwner = event.resource?.isOwner;
    const createdBy = event.resource?.createdBy?.username;
    const isDelivery = event.resource?.type === "delivery";
    const userName = event.resource?.user?.username;

    return (
      <div style={{ padding: "2px 4px" }}>
        <div style={{ fontWeight: "bold", fontSize: "11px" }}>
          {event.title}
        </div>
        {isDelivery && user.role === "admin" && userName && (
          <div style={{ fontSize: "9px", opacity: 0.8 }}>for {userName}</div>
        )}
        {!isDelivery && !isOwner && createdBy && (
          <div style={{ fontSize: "9px", opacity: 0.8 }}>by {createdBy}</div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="calendar-container">
      <div
        style={{
          marginBottom: "20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <h2>📅 Calendar</h2>
        <button
          className="btn btn-primary"
          onClick={() => {
            setSelectedEvent(null);
            setSelectedSlot({
              start: new Date(),
              end: moment().add(1, "hour").toDate(),
            });
            setShowModal(true);
          }}
        >
          Add Event
        </button>
      </div>

      <div
        style={{
          marginBottom: "15px",
          padding: "10px",
          backgroundColor: "#f8f9fa",
          borderRadius: "5px",
        }}
      >
        <h4>Legend:</h4>
        <div style={{ display: "flex", gap: "20px", marginTop: "5px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
            <div
              style={{
                width: "15px",
                height: "15px",
                backgroundColor: "#3174ad",
                borderRadius: "3px",
                fontWeight: "bold",
              }}
            ></div>
            <span>Your Events</span>
          </div>
          {user.role === "admin" && (
            <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
              <div
                style={{
                  width: "15px",
                  height: "15px",
                  backgroundColor: "#3174ad",
                  borderRadius: "3px",
                  opacity: 0.7,
                }}
              ></div>
              <span>Other Users' Events</span>
            </div>
          )}
          {/* Only show delivery legend for non-admin users */}
          {user.role !== "admin" && (
            <>
              <div
                style={{ display: "flex", alignItems: "center", gap: "5px" }}
              >
                <div
                  style={{
                    width: "15px",
                    height: "15px",
                    backgroundColor: "#28a745",
                    borderRadius: "3px",
                    border: "2px solid #20c997",
                  }}
                ></div>
                <span>Daily Deliveries</span>
              </div>
              <div
                style={{ display: "flex", alignItems: "center", gap: "5px" }}
              >
                <div
                  style={{
                    width: "15px",
                    height: "15px",
                    backgroundColor: "#28a745",
                    borderRadius: "3px",
                    opacity: 0.7,
                  }}
                ></div>
                <span>Public Events</span>
              </div>
            </>
          )}
        </div>
      </div>

      <div style={{ height: "600px", marginBottom: "20px" }}>
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          style={{ height: "100%" }}
          onSelectEvent={handleSelectEvent}
          onSelectSlot={handleSelectSlot}
          selectable={true}
          eventPropGetter={eventStyleGetter}
          components={{
            event: CustomEvent,
          }}
          views={["month", "week", "day", "agenda"]}
          defaultView="month"
          popup={true}
          step={30}
          showMultiDayTimes={true}
          onNavigate={(date) => setCalendarDate(date)}
          date={calendarDate}
        />
      </div>

      {showModal && (
        <EventModal
          event={selectedEvent}
          slot={selectedSlot}
          user={user}
          onClose={() => setShowModal(false)}
          onSave={handleEventSaved}
          onDelete={handleDeleteEvent}
        />
      )}

      {showDeliveryModal && (
        <DeliveryEventModal
          deliveryEvent={selectedDeliveryEvent}
          user={user}
          onClose={() => setShowDeliveryModal(false)}
          onSave={handleEventSaved}
        />
      )}
    </div>
  );
};

export default CalendarView;
